import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/api';

const AdminLeaderboard = () => {
  const [todayLeaderboard, setTodayLeaderboard] = useState([]);
  const [allTimeLeaderboard, setAllTimeLeaderboard] = useState([]);
  const [activeTab, setActiveTab] = useState('today');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState([{ name: 'all', displayName: 'All Categories' }]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [stats, setStats] = useState({
    todayParticipants: 0,
    allTimeParticipants: 0,
    todayAvgScore: 0,
    allTimeAvgScore: 0
  });

  const fetchLeaderboards = useCallback(async (categoryValueParam) => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('adminToken');
      const categoryValue = typeof categoryValueParam === 'string' ? categoryValueParam : 'all';
      const params = {};
      if (categoryValue && categoryValue !== 'all') {
        params.category = categoryValue;
      }

      // Fetch today's leaderboard
      const todayResponse = await axios.get(`${API_BASE_URL}/api/quiz/leaderboard/today`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setTodayLeaderboard(todayResponse.data || []);

      // Fetch all-time leaderboard
      const allTimeResponse = await axios.get(`${API_BASE_URL}/api/quiz/leaderboard/alltime`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setAllTimeLeaderboard(allTimeResponse.data || []);

      // Calculate stats
      const todayScores = todayResponse.data?.map(user => (user.score / (user.totalQuestions || 10)) * 100) || [];
      const allTimeScores = allTimeResponse.data?.map(user => user.averageScore) || [];

      setStats({
        todayParticipants: todayResponse.data?.length || 0,
        allTimeParticipants: allTimeResponse.data?.length || 0,
        todayAvgScore: todayScores.length ? todayScores.reduce((a, b) => a + b, 0) / todayScores.length : 0,
        allTimeAvgScore: allTimeScores.length ? allTimeScores.reduce((a, b) => a + b, 0) / allTimeScores.length : 0
      });

    } catch (error) {
      console.error('Error fetching leaderboard:', error);
      setError('Failed to load leaderboard data. Please try again.');
      if (error.response?.status === 401) {
        localStorage.removeItem('adminToken');
        window.location.href = '/admin/login';
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/quiz/categories`);
        const categoryOptions = Array.isArray(response.data) && response.data.length > 0
          ? response.data
          : [{ name: 'all', displayName: 'All Categories' }];

        setCategories(categoryOptions);

        const defaultCategory = categoryOptions.find(cat => cat.name === 'all')
          ? 'all'
          : categoryOptions[0].name;

        setSelectedCategory(defaultCategory);
        await fetchLeaderboards(defaultCategory);
      } catch (catError) {
        console.error('Error fetching categories:', catError);
        const fallbackCategories = [{ name: 'all', displayName: 'All Categories' }];
        setCategories(fallbackCategories);
        setSelectedCategory('all');
        await fetchLeaderboards('all');
      }
    };

    loadInitialData();
  }, [fetchLeaderboards]);

  const handleCategoryChange = (event) => {
    const value = event.target.value;
    setSelectedCategory(value);
    fetchLeaderboards(value);
  };

  const currentCategoryLabel = categories.find(cat => cat.name === selectedCategory)?.displayName || 'All Categories';
  const isAllCategory = selectedCategory === 'all';

  const formatTime = (seconds) => {
    if (!seconds) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getRankBadge = (rank) => {
    const baseClasses = "inline-flex items-center px-2 sm:px-2.5 py-0.5 rounded-full text-xs font-medium";
    switch (rank) {
      case 1:
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      case 2:
        return `${baseClasses} bg-gray-100 text-gray-800`;
      case 3:
        return `${baseClasses} bg-orange-100 text-orange-800`;
      default:
        return `${baseClasses} bg-blue-100 text-blue-800`;
    }
  };

  const getRankEmoji = (rank) => {
    switch (rank) {
      case 1: return '🥇';
      case 2: return '🥈';
      case 3: return '🥉';
      default: return '🏅';
    }
  };

  const currentLeaderboard = activeTab === 'today' ? todayLeaderboard : allTimeLeaderboard;
  const currentStats = activeTab === 'today' 
    ? { participants: stats.todayParticipants, avgScore: stats.todayAvgScore }
    : { participants: stats.allTimeParticipants, avgScore: stats.allTimeAvgScore };

  return (
    <div className="p-3 sm:p-4 lg:p-6">
      {/* **RESPONSIVE: Header** */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-6 space-y-3 sm:space-y-0">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Quiz Leaderboard</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1">Monitor and analyze quiz performance rankings</p>
          <div className="mt-2 text-xs sm:text-sm text-gray-500">
            Viewing <span className="font-semibold text-gray-900">{currentCategoryLabel}</span>
          </div>
        </div>
        <div className="w-full sm:w-auto flex flex-col sm:flex-row sm:items-center gap-2">
          <select
            value={selectedCategory}
            onChange={handleCategoryChange}
            aria-label="Filter leaderboard by category"
            className="w-full sm:w-48 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {categories.map((categoryOption) => (
              <option key={categoryOption.name} value={categoryOption.name}>
                {categoryOption.displayName || categoryOption.name}
              </option>
            ))}
          </select>
          <button
            onClick={() => fetchLeaderboards(selectedCategory)}
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700 text-white px-3 sm:px-4 py-2 rounded-lg font-medium disabled:opacity-50 flex items-center text-sm sm:text-base w-full sm:w-auto justify-center"
          >
            <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* **RESPONSIVE: Stats Cards** */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6 mb-4 sm:mb-6">
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border">
          <h3 className="text-xs sm:text-sm font-medium text-gray-500">
            {activeTab === 'today' ? "Today's" : 'All-Time'} Participants
          </h3>
          <p className="text-xl sm:text-2xl font-bold text-gray-900 mt-1">{currentStats.participants}</p>
        </div>
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border">
          <h3 className="text-xs sm:text-sm font-medium text-gray-500">Average Score</h3>
          <p className="text-xl sm:text-2xl font-bold text-gray-900 mt-1">
            {Math.round(currentStats.avgScore)}%
          </p>
        </div>
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border col-span-1 sm:col-span-2 lg:col-span-1">
          <h3 className="text-xs sm:text-sm font-medium text-gray-500">Top Score</h3>
          <p className="text-xl sm:text-2xl font-bold text-gray-900 mt-1">
            {currentLeaderboard.length > 0 
              ? activeTab === 'today' 
                ? `${Math.round((currentLeaderboard[0].score / (currentLeaderboard[0].totalQuestions || 10)) * 100)}%`
                : `${Math.round(currentLeaderboard[0].averageScore)}%`
              : '0%'
            }
          </p>
        </div>
      </div>

      {/* **RESPONSIVE: Tab Navigation** */}
      <div className="bg-white rounded-lg shadow-sm border mb-4 sm:mb-6 overflow-hidden">
        <div className="flex">
          <button
            onClick={() => setActiveTab('today')}
            className={`flex-1 px-3 sm:px-6 py-3 sm:py-4 text-center font-medium transition-colors text-sm sm:text-base ${
              activeTab === 'today'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            <span className="hidden sm:inline">Today's Rankings</span>
            <span className="sm:hidden">Today</span>
          </button>
          <button
            onClick={() => setActiveTab('alltime')}
            className={`flex-1 px-3 sm:px-6 py-3 sm:py-4 text-center font-medium transition-colors text-sm sm:text-base ${
              activeTab === 'alltime'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            <span className="hidden sm:inline">All-Time Rankings</span>
            <span className="sm:hidden">All-Time</span>
          </button>
        </div>
      </div>

      {/* **RESPONSIVE: Leaderboard Table/Cards** */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center p-8 sm:p-12">
            <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-sm sm:text-base text-gray-600">Loading leaderboard...</span>
          </div>
        ) : error ? (
          <div className="text-center py-8 sm:py-12 px-4">
            <div className="text-red-500 mb-4">
              <svg className="mx-auto h-10 w-10 sm:h-12 sm:w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-red-600 mb-4 text-sm sm:text-base">{error}</p>
            <button
              onClick={() => fetchLeaderboards(selectedCategory)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium text-sm sm:text-base"
            >
              Try Again
            </button>
          </div>
        ) : currentLeaderboard.length === 0 ? (
          <div className="text-center py-8 sm:py-12 px-4">
            <div className="text-gray-400 mb-4">
              <svg className="mx-auto h-10 w-10 sm:h-12 sm:w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </div>
            <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">
              {isAllCategory ? 'No data available' : `No ${currentCategoryLabel} data available`}
            </h3>
            <p className="text-sm sm:text-base text-gray-600">
              {activeTab === 'today' 
                ? isAllCategory
                  ? 'No quiz submissions for today yet.'
                  : `No ${currentCategoryLabel} quiz submissions for today yet.`
                : isAllCategory
                  ? 'No quiz data available in the system.'
                  : `No historical ${currentCategoryLabel} quiz data available yet.`
              }
            </p>
          </div>
        ) : (
          <>
            {/* **RESPONSIVE: Desktop Table View (hidden on mobile)** */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Rank
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Score
                    </th>
                    {activeTab === 'today' ? (
                      <>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Time Taken
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Completed At
                        </th>
                      </>
                    ) : (
                      <>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Total Quizzes
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Best Score
                        </th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {currentLeaderboard.map((user, index) => {
                    const rank = index + 1;
                    return (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className={getRankBadge(rank)}>
                            <span className="mr-1">{getRankEmoji(rank)}</span>
                            #{rank}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                                <span className="text-sm font-medium text-blue-600">
                                  {user.userName?.charAt(0)?.toUpperCase() || 'U'}
                                </span>
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {user.userName || 'Anonymous'}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {activeTab === 'today' 
                              ? (
                                <>
                                  <div className="font-semibold">
                                    {user.score}/{user.totalQuestions || 10}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {Math.round((user.score / (user.totalQuestions || 10)) * 100)}%
                                  </div>
                                </>
                              )
                              : (
                                <>
                                  <div className="font-semibold">
                                    {Math.round(user.averageScore)}%
                                  </div>
                                  <div className="text-xs text-gray-500">Average</div>
                                </>
                              )
                            }
                          </div>
                        </td>
                        {activeTab === 'today' ? (
                          <>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatTime(user.timeSpent)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {user.completedAt 
                                ? new Date(user.completedAt).toLocaleString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })
                                : 'N/A'
                              }
                            </td>
                          </>
                        ) : (
                          <>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {user.totalQuizzes} quiz{user.totalQuizzes !== 1 ? 'es' : ''}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {user.bestScore}/{user.totalQuestions || 10}
                              <div className="text-xs text-gray-500">
                                ({Math.round((user.bestScore / (user.totalQuestions || 10)) * 100)}%)
                              </div>
                            </td>
                          </>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* **RESPONSIVE: Mobile/Tablet Card View (visible on small screens)** */}
            <div className="lg:hidden">
              <div className="space-y-3 sm:space-y-4 p-3 sm:p-4">
                {currentLeaderboard.map((user, index) => {
                  const rank = index + 1;
                  return (
                    <div key={index} className="bg-gray-50 rounded-lg p-3 sm:p-4 hover:bg-gray-100 transition-colors">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <div className={getRankBadge(rank)}>
                            <span className="mr-1">{getRankEmoji(rank)}</span>
                            <span className="hidden sm:inline">#{rank}</span>
                          </div>
                          <div className="flex items-center space-x-2 sm:space-x-3">
                            <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-full bg-blue-100 flex items-center justify-center">
                              <span className="text-xs sm:text-sm font-medium text-blue-600">
                                {user.userName?.charAt(0)?.toUpperCase() || 'U'}
                              </span>
                            </div>
                            <div>
                              <div className="text-sm sm:text-base font-medium text-gray-900 truncate max-w-32 sm:max-w-none">
                                {user.userName || 'Anonymous'}
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        <div className="text-right">
                          <div className="text-base sm:text-lg font-bold text-gray-900">
                            {activeTab === 'today' 
                              ? `${Math.round((user.score / (user.totalQuestions || 10)) * 100)}%`
                              : `${Math.round(user.averageScore)}%`
                            }
                          </div>
                          {activeTab === 'today' && (
                            <div className="text-xs sm:text-sm text-gray-500">
                              {user.score}/{user.totalQuestions || 10}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3 text-xs sm:text-sm text-gray-600">
                        {activeTab === 'today' ? (
                          <>
                            <div>
                              <span className="font-medium">Time:</span> {formatTime(user.timeSpent)}
                            </div>
                            <div className="text-right">
                              <span className="font-medium">Completed:</span>
                              <div className="text-xs">
                                {user.completedAt 
                                  ? new Date(user.completedAt).toLocaleString('en-US', {
                                      month: 'short',
                                      day: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })
                                  : 'N/A'
                                }
                              </div>
                            </div>
                          </>
                        ) : (
                          <>
                            <div>
                              <span className="font-medium">Total Quizzes:</span> {user.totalQuizzes}
                            </div>
                            <div className="text-right">
                              <span className="font-medium">Best Score:</span>
                              <div>
                                {user.bestScore}/{user.totalQuestions || 10} ({Math.round((user.bestScore / (user.totalQuestions || 10)) * 100)}%)
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>

      {/* **RESPONSIVE: Results Summary** */}
      {currentLeaderboard.length > 0 && (
        <div className="mt-3 sm:mt-4 text-xs sm:text-sm text-gray-600 text-center">
          Showing {currentLeaderboard.length} {activeTab === 'today' ? 'participants' : 'users'}
          {!isAllCategory && ` in ${currentCategoryLabel}`}
        </div>
      )}
    </div>
  );
};

export default AdminLeaderboard;
